/**
 * Base64URL encoding of a UTF-8 string — unpadded, `-`/`_` alphabet.
 *
 * ## Why this exists as its own module
 *
 * The same value is produced in three places that must agree byte for byte:
 *
 *   1. `main-consumer/src/lib/email-trigger-service.ts` (`base64UrlUtf8`) —
 *      expands the `{{credentialsQuery:username:code}}` sentinel into the
 *      `?credentials=` query of every emailed login link.
 *   2. `parse-email.ts` (`base64UrlJson`) — encodes conditional-block and
 *      data-table markers into the stored template HTML.
 *   3. The guest drawer's "visit website" link and the QR code it feeds
 *      (`PGuestDetails/guest-actions/components/Actions.tsx`).
 *
 * Until 2026-08-21 the third used bare `btoa(username + ":" + code)`, which
 * differs from the other two in two ways that both reach real users:
 *
 *   - **Alphabet.** Standard base64 emits `+` and `/`. In a query string `+`
 *     is a space, so every reader carries a `.replace(/ /g, "+")` repair —
 *     `website-builder-frontend/src/App.tsx`, the builder's own
 *     `WebsiteBuilder/children/Login/Login.tsx`, and at least one
 *     customer-authored site. Three copies of one workaround for a value we
 *     control.
 *   - **Character set.** `btoa` encodes UTF-16 code units as Latin-1 bytes and
 *     throws outright above U+00FF. A username with an umlaut therefore
 *     encodes to different bytes here than in the email, so the two links for
 *     the same guest decode to different strings.
 *
 * The QR path is why this is worth fixing rather than documenting: that URL
 * gets printed on badges. A laminated card cannot be re-issued.
 *
 * Readers must keep accepting the standard-base64 form indefinitely — links
 * and QR codes minted before this change stay in circulation.
 */
export function base64UrlUtf8(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
